import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';

import { type Promotion, promotionsRequest } from 'api/promotions';
import ErrorEmptyState from 'components/EmptyStates/ErrorEmptyState';
import Loader from 'components/Loader';
import Meta from 'components/Meta';
import { useLanguage } from 'store';
import { getMediaUrl } from 'utils/assets';
import useLayout, { LayoutType } from 'utils/layout';
import { Translation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useRequest from 'utils/useRequest';

import styles from './PromotionsPage.module.scss';

const emptyPromotions: Array<Promotion> = [];

const PromotionsPage = () => {
  const [
    {
      current: { code: lang },
    },
  ] = useLanguage();
  const {
    data: promotions = emptyPromotions,
    loading,
    error,
  } = useRequest(() => promotionsRequest({ query: { lang } }), [lang]);
  const [selectedPromotion, setSelectedPromotion] = useState<
    Promotion | undefined
  >();
  const params = useParams();
  const layout = useLayout();
  const [, navigate] = useLocation();
  const isLoading =
    loading === LoadingState.initial || loading === LoadingState.loading;
  const isEmpty = !isLoading && !promotions.length && !error;
  const shouldRenderList =
    (layout !== LayoutType.mobile || !params?.id) &&
    !isLoading &&
    !isEmpty &&
    !error;

  useEffect(() => {
    if (layout !== LayoutType.mobile && !params?.id && promotions.length) {
      navigate(`/promotions/${promotions[0].id}`);
    }
  }, [layout, params, promotions, navigate]);

  useEffect(() => {
    setSelectedPromotion(
      promotions.find((promotion) => promotion.id === Number(params.id)),
    );
  }, [params.id, promotions]);

  return (
    <div className={styles.wrapper}>
      <Meta title={<Translation message="title_promotions" />} />
      {isLoading && <Loader />}
      {isEmpty && <div>No promotions found</div>}
      {error && <ErrorEmptyState />}
      {shouldRenderList && (
        <div className={styles.list}>
          {promotions.map((promotion) => (
            <div key={promotion.id} className={styles.item}>
              <Link to={`/promotions/${promotion.id}`}>
                <img
                  alt={promotion.title}
                  src={getMediaUrl(promotion.cover_image_url, 'cms')}
                />
                <span>{promotion.title}</span>
              </Link>
            </div>
          ))}
        </div>
      )}
      {selectedPromotion && (
        <div
          className={styles.promotion}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: data from CMS
          dangerouslySetInnerHTML={{
            __html: selectedPromotion.full_content,
          }}
        />
      )}
    </div>
  );
};

export default PromotionsPage;
